/**
 * Type mappers for converting database types to domain types.
 *
 * This module provides utilities to convert Supabase database types (snake_case, string dates)
 * to domain types (camelCase, Date objects) used throughout the application.
 *
 * @module typeMappers
 */

import type { Database } from "./database.types";
import type { User, Coordinates } from "./types";

// Type aliases for better readability
type SupabaseProfile = Database["public"]["Tables"]["profiles"]["Row"];

/**
 * Converts a Supabase profile row to the domain User type.
 *
 * Handles:
 * - snake_case → camelCase conversion
 * - string dates → Date objects
 * - JSON fields → typed objects
 * - null handling
 *
 * @param profile - The Supabase profile row from the database
 * @returns A User domain object
 *
 * @example
 * ```typescript
 * const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
 * if (data) {
 * const user = mapSupabaseProfileToUser(data);
 * // user is now a properly typed User object
 * }
 * ```
 */
export function mapSupabaseProfileToUser(profile: SupabaseProfile): User {
  // Parse coordinates if present
  let coordinates: Coordinates | null = null;
  if (profile.coordinates) {
    try {
      const coords = profile.coordinates as
        | { lat: number; lng: number }
        | Coordinates;
      if (typeof coords === "object" && "lat" in coords && "lng" in coords) {
        coordinates = { lat: coords.lat, lng: coords.lng };
      }
    } catch {
      // Invalid coordinates, leave as null
      coordinates = null;
    }
  }

  // Parse preferences if present
  let preferences: User["preferences"] | undefined;
  if (profile.preferences) {
    try {
      const prefs = profile.preferences as Record<string, unknown>;
      preferences = {
        emailNotifications: prefs.emailNotifications as boolean | undefined,
        pushNotifications: prefs.pushNotifications as boolean | undefined,
        marketingNotifications: prefs.marketingNotifications as
          | boolean
          | undefined,
      };
    } catch {
      // Invalid preferences, leave as undefined
      preferences = undefined;
    }
  }

  // Parse privacy settings if present
  let privacy: User["privacy"] | undefined;
  if (profile.privacy_settings) {
    try {
      const priv = profile.privacy_settings as Record<string, unknown>;
      privacy = {
        profileVisibility: priv.profileVisibility as boolean | undefined,
        activityVisibility: priv.activityVisibility as boolean | undefined,
      };
    } catch {
      // Invalid privacy settings, leave as undefined
      privacy = undefined;
    }
  }

  return {
    // Core identity fields
    id: profile.id,
    name: profile.name || "",
    email: profile.email || "",

    // Authentication & authorization
    isAdmin: profile.is_admin ?? false,
    // Note: isEmailVerified should come from auth.users table, not profiles
    // This will need to be set separately when mapping from auth context

    // Profile fields
    phone: profile.phone || undefined,
    location: profile.location || undefined,
    location_state_id: profile.location_state_id ?? null,
    location_lga_id: profile.location_lga_id ?? null,
    location_neighborhood_id: profile.location_neighborhood_id ?? null,
    coordinates,
    place_id: profile.place_id || undefined,
    avatar_url: profile.avatar_url || undefined,
    gender: profile.gender || undefined,
    is_profile_complete: profile.is_profile_complete ?? false,
    partner_type: profile.partner_type ?? null,
    discovery_channel: profile.discovery_channel || undefined,
    registrationOnboardingCompletedAt:
      profile.registration_onboarding_completed_at
        ? new Date(profile.registration_onboarding_completed_at)
        : null,

    // Preferences & Privacy
    preferences,
    privacy,

    // Timestamps (convert string to Date, handle nulls)
    createdAt: profile.created_at ? new Date(profile.created_at) : new Date(),
    updatedAt: profile.updated_at ? new Date(profile.updated_at) : new Date(),
  };
}

// Re-export existing mappers for consistency and discoverability
export { mapSupabaseItemToItem } from "./itemsAPI";
export { mapSupabaseItemRequestToItemRequest } from "./item-requests/mapItemRequest";

// Note: mapSupabaseTransactionToTransaction is currently private in transactionAPI.ts
// It's used internally by transactionAPI functions. If needed elsewhere, we can export it here.
